import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";

import { Button } from "@/components/ui/button";
import { 
    Download, 
    Smartphone, 
    Share, 
    PlusSquare, 
    ShieldCheck, 
    Wrench, 
    CalendarCheck, 
    ChevronRight,
    User,
    ChevronDown,
    Check
} from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";

const MarketingSite = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [showAllTiers, setShowAllTiers] = useState(false);
    const [maintenanceOpenIndex, setMaintenanceOpenIndex] = useState(-1);
    const [remindersOpenIndex, setRemindersOpenIndex] = useState(-1);
    const [directoryOpenIndex, setDirectoryOpenIndex] = useState(-1);
    const [storageOpenIndex, setStorageOpenIndex] = useState(-1);
    const [billingCycle, setBillingCycle] = useState('annual');
    const location = useLocation();



    useEffect(() => {
        if (location.hash === '#pricing') {
            const el = document.getElementById('pricing');
            if (el) {
                setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 100);
            }
        } else {
            window.scrollTo(0, 0);
        }
    }, [location]);

    const maintenanceFeatures = [
        { title: "Service Timeline", content: "Keep a permanent log of all your past services so you always know exactly when an appliance was last repaired." },
        { title: "Store Invoices", content: "Attach photos or PDFs of your receipts and invoices directly to the service record for safe keeping." },
        { title: "Automatic Reminders to Providers", content: "Set it and forget it. We'll automatically notify your trusted service providers when it's time for routine maintenance." }
    ];

    const remindersFeatures = [
        { title: "Recommended Actions", content: "We help you keep up to date with services due by reminding you as services get close, as well as when to water your plants." },
        { title: "Automated Provider Alerts", content: "We can automatically send emails to your trusted contractors when it's time for routine service." },
        { title: "Warranty Expirations", content: "Never miss a claim window again. Get notified weeks before your appliance or furniture warranties are about to expire." }
    ];

    const directoryFeatures = [
        { title: "Building the Directory", content: "Build your personal directory as you add more providers. Once a provider is verified, their contact info becomes publicly accessible to others." },
        { title: "Community Favourites", content: "Any time you or someone else uses a provider in the community, it shows up in a specified section under each category so you can see who your neighbors trust first." },
        { title: "Grow the Community", content: "You help grow the network! As more neighbors add their trusted professionals, the bigger and better the verified directory gets for everyone." }
    ];

    const storageFeatures = [
        { title: "Invoice & Receipt Uploads", content: "Take a photo or upload a PDF of your receipts and invoices. We attach them directly to the specific appliance or service log so you never lose them." },
        { title: "Warranty Tracking", content: "Log your purchase dates and warranty expiry. We automatically track when they expire and notify you on your dashboard before you miss out." },
        { title: "General Home Records", content: "Store your paint codes, water shutoff valve locations, and DB board photos in one central vault so you have them handy during emergencies." }
    ];



    return (
        <div className="min-h-screen w-full max-w-[100vw] bg-[#F8FAF9] text-slate-900 font-sans overflow-x-hidden selection:bg-emerald-500/30">
            {/* Background Glows for Hero (Optimized for performance) */}
            <div className="absolute top-0 left-[-10%] w-[40%] h-[40%] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(110,231,183,0.15) 0%, rgba(248,250,249,0) 70%)' }} />
            <div className="absolute top-40 right-[-10%] w-[40%] h-[40%] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(94,234,212,0.15) 0%, rgba(248,250,249,0) 70%)' }} />

            {/* Navbar */}
            <nav className="fixed top-0 left-0 right-0 z-50 px-6 py-4 border-b border-slate-200/50 bg-white/70 backdrop-blur-xl">
                <div className="max-w-6xl mx-auto flex items-center justify-between relative">
                    <div className="flex items-center gap-3 relative">
                        <Link 
                            to="/"
                            className="flex items-center gap-2 hover:opacity-80 transition-opacity outline-none"
                        >
                            <img src="/logo.png" alt="HomeEzi" className="w-8 h-8 rounded-lg" />
                            <span className="text-xl font-bold tracking-tight text-emerald-950">HomeEzi</span>
                        </Link>

                        <button 
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            className="flex items-center p-1.5 hover:opacity-80 transition-opacity outline-none bg-slate-100 rounded-md md:hidden"
                        >
                            <ChevronDown className={`w-4 h-4 text-emerald-950 transition-transform ${isMenuOpen ? 'rotate-180' : ''}`} />
                        </button>

                        <div className="hidden md:flex items-center gap-6 ml-4 border-l border-slate-200 pl-6">
                            <Link to="/features" className="text-sm font-semibold text-slate-600 hover:text-emerald-700 transition-colors">
                                Features
                            </Link>
                            <a href="#pricing" className="text-sm font-semibold text-slate-600 hover:text-emerald-700 transition-colors">
                                Pricing
                            </a>
                        </div>

                        <AnimatePresence>
                            {isMenuOpen && (
                                <motion.div 
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 10 }}
                                    className="absolute top-12 left-0 w-48 bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden flex flex-col py-2 z-50 md:hidden"
                                >
                                    <Link to="/features" onClick={() => setIsMenuOpen(false)} className="px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 hover:text-emerald-700 transition-colors">
                                        Features
                                    </Link>
                                    <a href="#pricing" onClick={() => setIsMenuOpen(false)} className="px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 hover:text-emerald-700 transition-colors">
                                        Pricing
                                    </a>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <main className="pt-28 relative z-10 flex flex-col justify-start min-h-[92vh]">
                
                {/* Header & Slogan Group - All in Frame */}
                <div className="max-w-4xl mx-auto text-center space-y-4 px-6 relative z-20 flex-none pt-4">
                    <img src="/logo.png" alt="HomeEzi" className="w-16 h-16 mx-auto rounded-[1.2rem] shadow-xl shadow-emerald-500/20 mb-6" />
                    <div>
                        <h1 className="text-[5rem] md:text-[8rem] leading-[0.9] font-extrabold tracking-tighter text-emerald-950">
                            HomeEzi
                        </h1>
                        <p className="text-xl md:text-2xl font-medium text-emerald-800/80 pt-2 pb-8">
                            Your home, pocket sized.
                        </p>
                    </div>
                </div>

                {/* Tight Fanned out Phones - Top Anchored beneath the Header */}
                <div className="relative w-full flex-1 pointer-events-none flex justify-center">
                    <div className="relative w-full max-w-5xl h-[550px] md:h-[600px]">
                        {/* Left Phone */}
                        <motion.img 
                            initial={{ y: 150, opacity: 0, x: '-50%' }}
                            animate={{ y: 50, opacity: 1, x: '-120%', rotate: -12 }}
                            transition={{ duration: 1, ease: "easeOut" }}
                            src="/mockup-real-grid.svg" 
                            className="absolute left-1/2 top-0 w-[45%] md:w-[340px] drop-shadow-2xl z-10"
                        />
                        {/* Right Phone */}
                        <motion.img 
                            initial={{ y: 150, opacity: 0, x: '-50%' }}
                            animate={{ y: 50, opacity: 1, x: '20%', rotate: 12 }}
                            transition={{ duration: 1, ease: "easeOut", delay: 0.2 }}
                            src="/mockup-real-directory.svg" 
                            className="absolute left-1/2 top-0 w-[45%] md:w-[340px] drop-shadow-2xl z-10"
                        />
                        {/* Center Phone */}
                        <motion.img 
                            initial={{ y: 150, opacity: 0, x: '-50%' }}
                            animate={{ y: 0, opacity: 1, x: '-50%' }}
                            transition={{ duration: 1, ease: "easeOut", delay: 0.1 }}
                            src="/mockup-real-dashboard.svg" 
                            fetchPriority="high"
                            className="absolute left-1/2 top-0 w-[45%] md:w-[340px] drop-shadow-[0_30px_60px_rgba(0,0,0,0.5)] z-30"
                        />
                    </div>
                </div>
            </main>

            {/* Feature Sections - Stacked Curved Cards */}
            <div className="relative w-full z-20">
                
                {/* Section 1: Maintenance History - Deep Teal */}
                <div id="features" className="w-full bg-teal-900 rounded-t-[3rem] px-6 py-20 md:py-32 -mt-24 md:-mt-32 shadow-[0_-20px_50px_rgba(0,0,0,0.1)] min-h-[92vh] flex items-center relative overflow-hidden">
                    {/* Top Peek Label */}
                    <div className="absolute top-8 left-1/2 -translate-x-1/2 flex items-center gap-2 text-teal-300/80 font-bold text-sm tracking-widest uppercase">
                        <Wrench className="w-4 h-4" />
                        Maintenance
                    </div>

                    <div className="absolute top-[-20%] right-[-10%] w-[50%] h-[50%] bg-teal-700/30 blur-[120px] rounded-full pointer-events-none" />
                    
                    <div className="max-w-6xl mx-auto flex flex-col-reverse md:flex-row items-center gap-12 md:gap-24 relative z-10 w-full">
                        <motion.div 
                            initial={{ opacity: 0, scale: 0.9, y: 40 }}
                            whileInView={{ opacity: 1, scale: 1, y: 0 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 0.7 }}
                            className="flex-1 w-full"
                        >
                            <img src="/LogService.svg" alt="Maintenance History Mockup" className="w-full max-w-[280px] mx-auto drop-shadow-[0_20px_50px_rgba(0,0,0,0.5)] hover:-translate-y-4 transition-transform duration-700 ease-out" />
                        </motion.div>
                        <motion.div 
                            initial={{ opacity: 0, x: 40 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 0.7 }}
                            className="flex-1 space-y-6"
                        >
                            <div className="w-14 h-14 rounded-3xl bg-teal-800/50 border border-teal-700 flex items-center justify-center">
                                <Wrench className="w-7 h-7 text-teal-300" />
                            </div>
                            <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight">
                                Flawless Service History.
                            </h2>
                            <p className="text-xl text-teal-100/80 leading-relaxed max-w-lg">
                                Log every repair and service call. Enjoy the complete peace of mind that comes with knowing your house is running smoothly.
                            </p>
                            <div className="space-y-3 pt-6">
                                {maintenanceFeatures.map((item, idx) => (
                                    <div key={idx} className="bg-teal-800/30 rounded-2xl overflow-hidden border border-teal-700/50">
                                        <button 
                                            onClick={() => setMaintenanceOpenIndex(maintenanceOpenIndex === idx ? -1 : idx)}
                                            className="w-full px-5 py-4 flex items-center justify-between text-left"
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className={`w-8 h-8 rounded-full flex flex-shrink-0 items-center justify-center transition-colors ${maintenanceOpenIndex === idx ? 'bg-teal-500 text-teal-950' : 'bg-teal-900/50 text-teal-300'}`}>
                                                    <ChevronRight className={`w-4 h-4 transition-transform duration-300 ${maintenanceOpenIndex === idx ? 'rotate-90' : ''}`} />
                                                </div>
                                                <span className="text-teal-50 font-medium text-lg">{item.title}</span>
                                            </div>
                                        </button>
                                        <AnimatePresence>
                                            {maintenanceOpenIndex === idx && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: 'auto', opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    className="px-5 pb-5 pl-[4.25rem] text-teal-100/70 text-sm md:text-base leading-relaxed"
                                                >
                                                    {item.content}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                </div>

                {/* Section 2: Smart Reminders - Bright Emerald/White */}
                <div className="w-full bg-white rounded-t-[3rem] px-6 py-20 md:py-32 -mt-16 md:-mt-20 shadow-[0_-20px_50px_rgba(0,0,0,0.05)] min-h-[92vh] flex items-center relative overflow-hidden">
                    {/* Top Peek Label */}
                    <div className="absolute top-8 left-1/2 -translate-x-1/2 flex items-center gap-2 text-emerald-600/80 font-bold text-sm tracking-widest uppercase">
                        <CalendarCheck className="w-4 h-4" />
                        Reminders
                    </div>

                    <div className="absolute top-0 right-0 w-[40%] h-[100%] bg-emerald-50/50 blur-[100px] rounded-full pointer-events-none" />
                    
                    <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-12 md:gap-24 relative z-10 w-full">
                        <motion.div 
                            initial={{ opacity: 0, x: -40 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 0.7 }}
                            className="flex-1 space-y-6"
                        >
                            <div className="w-14 h-14 rounded-3xl bg-emerald-100 border border-emerald-200 flex items-center justify-center">
                                <CalendarCheck className="w-7 h-7 text-emerald-600" />
                            </div>
                            <h2 className="text-4xl md:text-5xl font-bold text-emerald-950 leading-tight">
                                Smart Reminders.
                            </h2>
                            <p className="text-xl text-slate-600 leading-relaxed max-w-lg">
                                Stay on top of home maintenance with automated reminders about routine services and plant watering.
                            </p>
                            <div className="space-y-3 pt-6">
                                {remindersFeatures.map((item, idx) => (
                                    <div key={idx} className="bg-emerald-50 rounded-2xl overflow-hidden border border-emerald-100">
                                        <button 
                                            onClick={() => setRemindersOpenIndex(remindersOpenIndex === idx ? -1 : idx)}
                                            className="w-full px-5 py-4 flex items-center justify-between text-left"
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className={`w-8 h-8 rounded-full flex flex-shrink-0 items-center justify-center transition-colors ${remindersOpenIndex === idx ? 'bg-emerald-600 text-white' : 'bg-emerald-100 text-emerald-600'}`}>
                                                    <ChevronRight className={`w-4 h-4 transition-transform duration-300 ${remindersOpenIndex === idx ? 'rotate-90' : ''}`} />
                                                </div>
                                                <span className="text-slate-800 font-medium text-lg">{item.title}</span>
                                            </div>
                                        </button>
                                        <AnimatePresence>
                                            {remindersOpenIndex === idx && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: 'auto', opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    className="px-5 pb-5 pl-[4.25rem] text-slate-600 text-sm md:text-base leading-relaxed"
                                                >
                                                    {item.content}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                        <motion.div 
                            initial={{ opacity: 0, scale: 0.9, y: 40 }}
                            whileInView={{ opacity: 1, scale: 1, y: 0 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 0.7 }}
                            className="flex-1 w-full"
                        >
                            <img src="/mockup-real-dashboard.svg" alt="Smart Reminders Mockup" className="w-full max-w-[280px] mx-auto drop-shadow-[0_30px_60px_rgba(0,0,0,0.15)] hover:-translate-y-4 transition-transform duration-700 ease-out" />
                        </motion.div>
                    </div>
                </div>

                {/* Section 3: Providers Directory - Dark Blue */}
                <div className="w-full bg-blue-950 rounded-t-[3rem] px-6 py-20 md:py-32 -mt-16 md:-mt-20 shadow-[0_-20px_50px_rgba(0,0,0,0.1)] min-h-[92vh] flex items-center relative overflow-hidden">
                    {/* Top Peek Label */}
                    <div className="absolute top-8 left-1/2 -translate-x-1/2 flex items-center gap-2 text-blue-300/80 font-bold text-sm tracking-widest uppercase">
                        <User className="w-4 h-4" />
                        Directory
                    </div>

                    <div className="absolute top-[-20%] right-[-10%] w-[50%] h-[50%] bg-blue-700/30 blur-[120px] rounded-full pointer-events-none" />
                    
                    <div className="max-w-6xl mx-auto flex flex-col-reverse md:flex-row items-center gap-12 md:gap-24 relative z-10 w-full">
                        <motion.div 
                            initial={{ opacity: 0, scale: 0.9, y: 40 }}
                            whileInView={{ opacity: 1, scale: 1, y: 0 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 0.7 }}
                            className="flex-1 w-full"
                        >
                            <img src="/mockup-real-directory.png" alt="Providers Directory Mockup" className="w-full max-w-[320px] mx-auto drop-shadow-[0_20px_50px_rgba(0,0,0,0.5)] hover:-translate-y-4 transition-transform duration-700 ease-out" />
                        </motion.div>
                        <motion.div 
                            initial={{ opacity: 0, x: 40 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 0.7 }}
                            className="flex-1 space-y-6"
                        >
                            <div className="w-14 h-14 rounded-3xl bg-blue-800/50 border border-blue-700 flex items-center justify-center">
                                <User className="w-7 h-7 text-blue-300" />
                            </div>
                            <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight">
                                Trusted Community Providers.
                            </h2>
                            <p className="text-xl text-blue-100/80 leading-relaxed max-w-lg">
                                Build your personal scope of trusted contractors. Add your own providers or discover exactly who your neighbors and community members are using for their repairs.
                            </p>
                            <div className="space-y-3 pt-6">
                                {directoryFeatures.map((item, idx) => (
                                    <div key={idx} className="bg-blue-800/30 rounded-2xl overflow-hidden border border-blue-700/50">
                                        <button 
                                            onClick={() => setDirectoryOpenIndex(directoryOpenIndex === idx ? -1 : idx)}
                                            className="w-full px-5 py-4 flex items-center justify-between text-left"
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className={`w-8 h-8 rounded-full flex flex-shrink-0 items-center justify-center transition-colors ${directoryOpenIndex === idx ? 'bg-blue-500 text-blue-950' : 'bg-blue-900/50 text-blue-300'}`}>
                                                    <ChevronRight className={`w-4 h-4 transition-transform duration-300 ${directoryOpenIndex === idx ? 'rotate-90' : ''}`} />
                                                </div>
                                                <span className="text-blue-50 font-medium text-lg">{item.title}</span>
                                            </div>
                                        </button>
                                        <AnimatePresence>
                                            {directoryOpenIndex === idx && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: 'auto', opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    className="px-5 pb-5 pl-[4.25rem] text-blue-100/70 text-sm md:text-base leading-relaxed"
                                                >
                                                    {item.content}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                </div>

                {/* Section 4: Document Storage - Dark Emerald */}
                <div className="w-full bg-emerald-950 rounded-t-[3rem] px-6 py-20 md:py-32 -mt-16 md:-mt-20 shadow-[0_-20px_50px_rgba(0,0,0,0.1)] min-h-[92vh] flex items-center relative overflow-hidden">
                    {/* Top Peek Label */}
                    <div className="absolute top-8 left-1/2 -translate-x-1/2 flex items-center gap-2 text-emerald-400/80 font-bold text-sm tracking-widest uppercase">
                        <ShieldCheck className="w-4 h-4" />
                        Document Storage
                    </div>

                    {/* Decorative blurred blob */}
                    <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-emerald-800/30 blur-[120px] rounded-full pointer-events-none" />
                    
                    <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-12 md:gap-24 relative z-10 w-full">
                        <motion.div 
                            initial={{ opacity: 0, x: -40 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 0.7 }}
                            className="flex-1 space-y-6"
                        >
                            <div className="w-14 h-14 rounded-3xl bg-emerald-900/50 border border-emerald-800 flex items-center justify-center">
                                <ShieldCheck className="w-7 h-7 text-emerald-400" />
                            </div>
                            <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight">
                                Complete Document Storage.
                            </h2>
                            <p className="text-xl text-emerald-100/80 leading-relaxed max-w-lg">
                                Store purchase receipts, invoices, serial numbers, and warranty documents securely in the cloud. Access them instantly whenever you need them.
                            </p>
                            <div className="space-y-3 pt-6">
                                {storageFeatures.map((item, idx) => (
                                    <div key={idx} className="bg-emerald-900/30 rounded-2xl overflow-hidden border border-emerald-800/50">
                                        <button 
                                            onClick={() => setStorageOpenIndex(storageOpenIndex === idx ? -1 : idx)}
                                            className="w-full px-5 py-4 flex items-center justify-between text-left"
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className={`w-8 h-8 rounded-full flex flex-shrink-0 items-center justify-center transition-colors ${storageOpenIndex === idx ? 'bg-emerald-500 text-emerald-950' : 'bg-emerald-900/50 text-emerald-400'}`}>
                                                    <ChevronRight className={`w-4 h-4 transition-transform duration-300 ${storageOpenIndex === idx ? 'rotate-90' : ''}`} />
                                                </div>
                                                <span className="text-emerald-50 font-medium text-lg">{item.title}</span>
                                            </div>
                                        </button>
                                        <AnimatePresence>
                                            {storageOpenIndex === idx && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: 'auto', opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    className="px-5 pb-5 pl-[4.25rem] text-emerald-100/70 text-sm md:text-base leading-relaxed"
                                                >
                                                    {item.content}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                        <motion.div 
                            initial={{ opacity: 0, scale: 0.9, y: 40 }}
                            whileInView={{ opacity: 1, scale: 1, y: 0 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 0.7 }}
                            className="flex-1 w-full"
                        >
                            <img src="/DocStorage.svg" alt="Document Storage Mockup" className="w-full max-w-[280px] mx-auto drop-shadow-[0_20px_50px_rgba(0,0,0,0.5)] hover:-translate-y-4 transition-transform duration-700 ease-out" />
                        </motion.div>
                    </div>
                </div>

                {/* Section 5: Pricing - Light Minimalist */}
                <div id="pricing" className="w-full bg-slate-50 rounded-t-[3rem] px-6 py-20 md:py-32 -mt-16 md:-mt-20 shadow-[0_-20px_50px_rgba(0,0,0,0.1)] relative overflow-hidden">
                    <div className="max-w-6xl mx-auto w-full relative z-10">
                        <div className="text-center mb-12 space-y-4">
                            <h2 className="text-4xl md:text-5xl font-bold text-slate-900">Simple, transparent pricing.</h2>
                            <p className="text-xl text-slate-500 max-w-2xl mx-auto">Everything you need to manage your home, packed into one simple subscription.</p>
                        </div>

                        {/* Billing Toggle */}
                        <div className="flex justify-center mb-12">
                            <div className="bg-white p-1.5 rounded-full inline-flex items-center relative border border-slate-200 shadow-sm">
                                <button
                                    onClick={() => setBillingCycle('monthly')}
                                    className={cn(
                                        "px-6 py-2.5 rounded-full text-sm font-bold transition-all z-10",
                                        billingCycle === 'monthly' ? "bg-slate-900 shadow-md text-white" : "text-slate-500 hover:text-slate-900"
                                    )}
                                >
                                    Monthly
                                </button>
                                <button
                                    onClick={() => setBillingCycle('annual')}
                                    className={cn(
                                        "px-6 py-2.5 rounded-full text-sm font-bold transition-all z-10 flex items-center gap-2",
                                        billingCycle === 'annual' ? "bg-slate-900 shadow-md text-white" : "text-slate-500 hover:text-slate-900"
                                    )}
                                >
                                    Annually
                                    <span className={cn(
                                        "text-[10px] uppercase tracking-wider font-extrabold px-2 py-0.5 rounded-full transition-colors",
                                        billingCycle === 'annual' ? "bg-emerald-500 text-white" : "bg-emerald-100 text-emerald-700"
                                    )}>
                                        2 Mos Free
                                    </span>
                                </button>
                            </div>
                        </div>

                        <div className="flex flex-wrap justify-center items-center gap-6 max-w-7xl mx-auto">
                            {/* Free */}
                            <div className="w-full md:w-[300px] bg-white rounded-3xl p-8 border border-slate-100 shadow-lg flex flex-col self-stretch">
                                <h3 className="text-xl font-bold text-slate-900">Free</h3>
                                <div className="mt-4 flex items-baseline text-5xl font-extrabold text-emerald-950">
                                    R0<span className="ml-1 text-xl font-medium text-slate-500">/mo</span>
                                </div>
                                <p className="mt-4 text-sm text-slate-500">Perfect for getting started.</p>
                                <ul className="mt-8 space-y-4 flex-1">
                                    {['1 Property', 'Basic Home Records (5 items)', '1 Service Log per Appliance'].map((feature) => (
                                        <li key={feature} className="flex items-start gap-3">
                                            <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                                            <span className="text-slate-700 text-sm">{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                                <Button 
                                    className="mt-8 w-full bg-slate-100 hover:bg-slate-200 text-slate-900 rounded-full h-12 font-bold" 
                                    onClick={() => window.location.href = "mailto:info@homeezi.co.za?subject=HomeEzi Basic Plan Inquiry"}
                                >
                                    Inquire Now
                                </Button>
                            </div>

                            {/* Essential (Popular) */}
                            <div className="w-full md:w-[320px] bg-emerald-950 rounded-3xl p-8 md:py-10 border border-emerald-900 shadow-2xl flex flex-col relative z-10 transform md:-translate-y-2">
                                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-emerald-500 text-white px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wide">
                                    Most Popular
                                </div>
                                <h3 className="text-xl font-bold text-white">Essential</h3>
                                <div className="mt-4 flex items-baseline text-5xl font-extrabold text-white">
                                    R{billingCycle === 'annual' ? '490' : '49'}<span className="ml-1 text-xl font-medium text-emerald-300">/{billingCycle === 'annual' ? 'yr' : 'mo'}</span>
                                </div>
                                <p className="mt-4 text-sm text-emerald-200">For the smart homeowner.</p>
                                <ul className="mt-8 space-y-4 flex-1">
                                    {['1 Property', 'Unlimited Home Records', 'Full Service History', 'Smart Reminders'].map((feature) => (
                                        <li key={feature} className="flex items-start gap-3">
                                            <Check className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                                            <span className="text-emerald-50 text-sm">{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                                <Button 
                                    className="mt-8 w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-bold rounded-full h-12 shadow-[0_10px_20px_rgba(16,185,129,0.2)] transition-transform hover:scale-105 active:scale-95" 
                                    onClick={() => window.location.href = "mailto:info@homeezi.co.za?subject=HomeEzi Essential Plan Inquiry"}
                                >
                                    Inquire Now
                                </Button>
                            </div>

                            {/* Investor */}
                            <div className="w-full md:w-[300px] bg-white rounded-3xl p-8 border border-slate-100 shadow-lg flex flex-col self-stretch">
                                <h3 className="text-xl font-bold text-slate-900">Investor</h3>
                                <div className="mt-4 flex items-baseline text-5xl font-extrabold text-emerald-950">
                                    R{billingCycle === 'annual' ? '1290' : '129'}<span className="ml-1 text-xl font-medium text-slate-500">/{billingCycle === 'annual' ? 'yr' : 'mo'}</span>
                                </div>
                                <p className="mt-4 text-sm text-slate-500">For multiple properties.</p>
                                <ul className="mt-8 space-y-4 flex-1">
                                    {['Up to 3 Properties', 'Everything in Essential'].map((feature) => (
                                        <li key={feature} className="flex items-start gap-3">
                                            <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                                            <span className="text-slate-700 text-sm">{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                                <Button 
                                    className="mt-8 w-full bg-slate-100 hover:bg-slate-200 text-slate-900 rounded-full h-12 font-bold" 
                                    onClick={() => window.location.href = "mailto:info@homeezi.co.za?subject=HomeEzi Investor Plan Inquiry"}
                                >
                                    Inquire Now
                                </Button>
                            </div>

                            <AnimatePresence>
                                {showAllTiers && (
                                    <>
                                        {/* Professional */}
                                        <motion.div 
                                            initial={{ opacity: 0, scale: 0.9, height: 0 }}
                                            animate={{ opacity: 1, scale: 1, height: 'auto' }}
                                            exit={{ opacity: 0, scale: 0.9, height: 0 }}
                                            className="w-full md:w-[300px] bg-white rounded-3xl p-8 border border-slate-100 shadow-lg flex flex-col self-stretch"
                                        >
                                            <h3 className="text-xl font-bold text-slate-900">Professional</h3>
                                            <div className="mt-4 flex items-baseline text-5xl font-extrabold text-emerald-950">
                                                R{billingCycle === 'annual' ? '1990' : '199'}<span className="ml-1 text-xl font-medium text-slate-500">/{billingCycle === 'annual' ? 'yr' : 'mo'}</span>
                                            </div>
                                            <p className="mt-4 text-sm text-slate-500">For growing portfolios.</p>
                                            <ul className="mt-8 space-y-4 flex-1">
                                                {['Up to 5 Properties', 'Property Manager Dashboard', 'Everything in Investor'].map((feature) => (
                                                    <li key={feature} className="flex items-start gap-3">
                                                        <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                                                        <span className="text-slate-700 text-sm">{feature}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                            <Button 
                                                className="mt-8 w-full bg-slate-100 hover:bg-slate-200 text-slate-900 rounded-full h-12 font-bold" 
                                                onClick={() => window.location.href = "mailto:info@homeezi.co.za?subject=HomeEzi Professional Plan Inquiry"}
                                            >
                                                Inquire Now
                                            </Button>
                                        </motion.div>

                                        {/* Estate */}
                                        <motion.div 
                                            initial={{ opacity: 0, scale: 0.9, height: 0 }}
                                            animate={{ opacity: 1, scale: 1, height: 'auto' }}
                                            exit={{ opacity: 0, scale: 0.9, height: 0 }}
                                            className="w-full md:w-[300px] bg-slate-900 rounded-3xl p-8 border border-slate-800 shadow-lg flex flex-col self-stretch text-white relative overflow-hidden"
                                        >
                                            <h3 className="text-xl font-bold text-white relative z-10">Estate</h3>
                                            <div className="mt-4 flex items-baseline text-3xl font-extrabold text-teal-400 relative z-10">
                                                Contact Us
                                            </div>
                                            <p className="mt-4 text-sm text-slate-400 relative z-10">For unlimited scaling.</p>
                                            <ul className="mt-8 space-y-4 flex-1 relative z-10">
                                                {['Unlimited Properties', 'Team Access', 'Custom Integrations'].map((feature) => (
                                                    <li key={feature} className="flex items-start gap-3">
                                                        <Check className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                                                        <span className="text-slate-300 text-sm">{feature}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                            <Button 
                                                className="mt-8 w-full bg-teal-500 hover:bg-teal-600 text-teal-950 rounded-full h-12 font-bold relative z-10" 
                                                onClick={() => window.location.href = "mailto:info@homeezi.co.za?subject=HomeEzi Estate Plan Inquiry"}
                                            >
                                                Contact Sales
                                            </Button>
                                        </motion.div>
                                    </>
                                )}
                            </AnimatePresence>
                        </div>

                        <div className="mt-12 text-center relative z-10">
                            <Button 
                                variant="outline" 
                                onClick={() => setShowAllTiers(!showAllTiers)}
                                className="rounded-full border-slate-200 text-slate-600 hover:bg-slate-100 h-12 px-8 font-semibold shadow-sm bg-white"
                            >
                                {showAllTiers ? 'Show Less' : 'View More Plans'}
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <footer className="bg-slate-950 pt-32 pb-12 -mt-16 md:-mt-24 rounded-t-[3rem] text-center text-slate-500 relative z-0 flex flex-col items-center justify-center gap-4">
                    <div className="mb-4">
                        <h4 className="text-white font-semibold mb-2">Contact Us</h4>
                        <div className="flex flex-col gap-2 text-sm">
                            <p>For general inquiries: <a href="mailto:info@homeezi.co.za?subject=General%20Inquiry" className="text-emerald-400 hover:text-emerald-300 font-medium transition-colors">info@homeezi.co.za</a></p>
                            <p>Are you a service provider wanting to join our directory? <a href="mailto:info@homeezi.co.za?subject=Provider%20Inquiry" className="text-emerald-400 hover:text-emerald-300 font-medium transition-colors">Apply here</a></p>
                        </div>
                    </div>
                    <p>© {new Date().getFullYear()} HomeEzi. All rights reserved.</p>
                    <div className="flex gap-4 text-sm">
                        <Link to="/privacy" className="hover:text-slate-300 transition-colors">Privacy Policy</Link>
                        <Link to="/cookie-policy" className="hover:text-slate-300 transition-colors">Cookie Policy</Link>
                    </div>
                </footer>
            </div>


        </div>
    );
};

export default MarketingSite;
